/**
 * Budget allocation API (USAGE-AND-BUDGET.md §5/§7b/P5).
 *
 * Each level allocates a points slice to its children — org→tenants,
 * tenant→networks, network→rooms. GET returns children with allocated +
 * remaining + the parent budget; PUT validates conservation (Σ children ≤
 * parent, 409 on violation) and saves. Allocations are stored as either a
 * percentage of the parent's live budget (``mode:'pct'``, auto-rescales) or an
 * absolute point count (``mode:'abs'``).
 */
import { getRestApiBearerToken } from '@/services/apiToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

function _enc(s) {
  return encodeURIComponent(String(s || ''));
}

async function _req(path, init) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...((init && init.headers) || {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.error || body.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = body; // carries conservation detail (allocatedTotal/parentBudget) on 409
    throw err;
  }
  return body;
}

/** Resolve the {GET,PUT} path for a budget-allocation scope. */
function _path(scope, id) {
  if (scope === 'org') return `/orgs/${_enc(id)}/budget-allocation`;
  if (scope === 'tenant') return `/tenants/${_enc(id)}/budget-allocation`;
  if (scope === 'network') return `/networks/${_enc(id)}/budget-allocation`;
  throw new Error(`unknown budget scope: ${scope}`);
}

/**
 * @param {'org'|'tenant'|'network'} scope
 * @param {string} id  orgId | tenantId | networkId
 * @returns {Promise<{level, levelId, parentBudgetPoints, allocatedTotalPoints, remainingPoints, unlimited, children: Array}>}
 */
export async function getBudgetAllocation(scope, id) {
  return _req(_path(scope, id), { method: 'GET' });
}

/**
 * @param {'org'|'tenant'|'network'} scope
 * @param {string} id
 * @param {Record<string, {mode:'pct'|'abs', value:number}>} allocations
 */
export async function putBudgetAllocation(scope, id, allocations) {
  return _req(_path(scope, id), {
    method: 'PUT',
    body: JSON.stringify({ allocations: allocations || {} }),
  });
}

/**
 * Room budget snapshot for the room-header gauge: the room's allocated +
 * month-to-date consumed points, derived from the network's allocation GET
 * (which carries per-room ``allocatedPoints`` + ``consumedPoints``). Returns
 * null when the room has no allocation (no gauge to show).
 *
 * @param {string} networkId
 * @param {string} poolId  the room's pool id
 * @returns {Promise<{allocated:number, consumed:number, pct:number}|null>}
 */
export async function getRoomBudget(networkId, poolId) {
  if (!networkId || !poolId) return null;
  let body;
  try {
    body = await getBudgetAllocation('network', networkId);
  } catch {
    return null;
  }
  const row = (body.children || []).find((c) => c.id === poolId);
  const allocated = Number(row?.allocatedPoints) || 0;
  if (allocated <= 0) return null; // unallocated room → no ceiling, no gauge
  const consumed = Number(row?.consumedPoints) || 0;
  const pct = Math.max(0, Math.min(100, Math.round((consumed / allocated) * 100)));
  return { allocated, consumed, pct };
}

export const _internal = { _path, API_BASE_URL };
